import { Flex, LoadingSpinner, Text } from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { Button } from 'src/design_system/components/Button/Button'
import { useSelector, useState } from 'src/hooks'
import MembershipRewardsConfigurator from '../MembershipRewardsConfigurator/MembershipRewardsConfigurator'
import { formatAmount } from 'src/helpers/payments'

export type MRStepTypes = 'initial' | 'configuring' | 'applied'

export interface MembershipRewardsToolProps {
  containerMaxWidth: string
  totalMRAvailable: number
  maxUsableMRs: number
  currentMR: number
  onMRUpdate: (newRPAmount: number) => Promise<MRStepTypes>
  onAddRecipients?: () => void
}

const MINIMUM_MEMBERMSHIP_REWARD = 50

const MembershipRewardsTool: React.FC<MembershipRewardsToolProps> = props => {
  const {
    containerMaxWidth,
    totalMRAvailable,
    maxUsableMRs,
    currentMR,
    onMRUpdate,
    onAddRecipients,
  } = props
  const [currentStep, setCurrentStep] = useState<MRStepTypes>(
    currentMR > 0 ? 'applied' : 'initial',
  )
  const [isRemovingMR, setIsRemovingMR] = useState(false)

  const { isMobile } = useSelector(state => state.window)

  const hasMinimumMR = totalMRAvailable > MINIMUM_MEMBERMSHIP_REWARD

  const onMembershipRewardsUpdate = async (newRPAmount: number) => {
    const updateResult = await onMRUpdate(newRPAmount)
    setCurrentStep(updateResult)
    setIsRemovingMR(false)
  }

  const step = {
    initial: (
      <Flex
        flexDirection="column"
        alignItems="center"
        inset="x4"
        style={{
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #d4f0ef, #f9dde6)',
        }}
      >
        <Flex
          flexDirection="column"
          outset={{ bottom: 'x4' }}
          alignItems="center"
        >
          <Text
            type="caption"
            content="You have"
            style={{ fontSize: '1.4rem', fontWeight: '500' }}
          />
          <Text
            type="body"
            weight="bold"
            content={totalMRAvailable}
            inset={'x2'}
            style={{
              fontSize: '3rem',
              color: '#404040',
              fontWeight: '700',
              lineHeight: '3rem',
            }}
          />
          <Text
            type="caption"
            content="Membership Rewards Available"
            style={{ fontSize: '1.4rem', fontWeight: '500' }}
            alignment="center"
          />
        </Flex>
        <Button
          title={
            onAddRecipients
              ? 'Add recipients to apply Membership Rewards'
              : 'Use them now and save more'
          }
          backgroundColor="#404040"
          onClick={() => {
            if (!!onAddRecipients) onAddRecipients()
            else setCurrentStep('configuring')
          }}
          maxWidth="85dvw"
        />
      </Flex>
    ),
    configuring: (
      <MembershipRewardsConfigurator
        currentMR={currentMR}
        maxUsableMRs={maxUsableMRs}
        onMRUpdate={onMembershipRewardsUpdate}
        totalMRAvailable={totalMRAvailable}
        onCancel={() => {
          setCurrentStep(currentMR === 0 ? 'initial' : 'applied')
        }}
      />
    ),
    applied: (
      <Flex
        flexDirection="column"
        inset={{ top: 'x2', bottom: 'x2', horizontal: 'x2' }}
        style={{
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #d4f0ef, #f9dde6)',
        }}
      >
        <Text
          type="body"
          content="Applied Rewards"
          weight="bold"
          style={{ color: '#404040', fontSize: '1.2rem' }}
        />
        {!isRemovingMR ? (
          <>
            <Flex
              width="100%"
              justifyContent="space-between"
              outset={{ top: 'x2' }}
            >
              <Text
                type="body"
                content={`Membership Rewards (${currentMR}):`}
                style={{ fontWeight: '500' }}
              />
              <Text
                type="body"
                content={`-$${formatAmount(currentMR)}`}
                weight="bold"
                style={{ color: '#404040' }}
              />
            </Flex>
            <Flex>
              <div
                onClick={() => {
                  setCurrentStep('configuring')
                }}
                style={{ cursor: 'pointer' }}
              >
                <Text
                  type="caption"
                  content="Edit"
                  style={{ textDecoration: 'underline' }}
                />
              </div>
              <div
                onClick={() => {
                  onMembershipRewardsUpdate(0)
                  setIsRemovingMR(true)
                }}
                style={{ cursor: 'pointer', marginLeft: '12px' }}
              >
                <Text
                  type="caption"
                  content="Remove"
                  style={{ textDecoration: 'underline' }}
                />
              </div>
            </Flex>
          </>
        ) : (
          <div>
            <LoadingSpinner size="large" />
          </div>
        )}
      </Flex>
    ),
  }[currentStep]

  return (
    <>
      {hasMinimumMR ? (
        <Flex
          maxWidth={containerMaxWidth}
          flexDirection="column"
          width="100%"
          outset={{
            right: !isMobile ? 'x4' : 'x2',
            bottom: 'x2',
            left: isMobile ? 'x2' : undefined,
          }}
        >
          {step}
        </Flex>
      ) : null}
    </>
  )
}

export default MembershipRewardsTool
